import { useEffect, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Button } from '@deepguide-ai/ui/button';
import { Checkbox } from '@deepguide-ai/ui/checkbox';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@deepguide-ai/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@deepguide-ai/ui/select';
import { queryClient, trpc } from '@/lib/trpc';
import type { ConfigModalProps, IntegrationProvider } from '../types';
import { openOAuthConnectPopup } from '../oauth-connect-lifecycle';

function AsanaConfigModal({ open, onOpenChange, integration, workspaceId }: ConfigModalProps) {
  const integrationId = integration?.id ?? '';
  const [workspaceGid, setWorkspaceGid] = useState('');
  const [projectGids, setProjectGids] = useState<string[]>([]);
  const [offset, setOffset] = useState(0);
  const [connecting, setConnecting] = useState(false);
  const scope = useQuery({ ...trpc.integrations.asana.getScope.queryOptions({ integrationId }), enabled: open && !!integrationId,
    refetchInterval: open && integrationId ? 3000 : false });
  const workspaces = useQuery({ ...trpc.integrations.asana.workspaces.queryOptions({ integrationId }), enabled: open && !!integrationId });
  const projects = useQuery({ ...trpc.integrations.asana.projects.queryOptions({ integrationId, workspaceGid }), enabled: open && !!integrationId && !!workspaceGid });
  const tasks = useQuery({ ...trpc.integrations.asana.tasks.queryOptions({ integrationId, offset }), enabled: open && !!integrationId });
  const save = useMutation(trpc.integrations.asana.saveScope.mutationOptions());
  const sync = useMutation(trpc.integrations.asana.sync.mutationOptions());
  const busy = !!scope.data?.syncState && !['completed', 'failed'].includes(scope.data.syncState);

  useEffect(() => {
    if (scope.data?.scope) {
      setWorkspaceGid(scope.data.scope.workspaceGid);
      setProjectGids(scope.data.scope.projects.map(project => project.gid));
    }
  }, [scope.data?.scope]);
  useEffect(() => {
    void queryClient.invalidateQueries({ queryKey: trpc.integrations.asana.tasks.queryKey() });
  }, [scope.data?.lastSyncedAt]);

  const error = scope.error || workspaces.error || projects.error || tasks.error;
  const saveAndSync = async () => {
    try {
      await save.mutateAsync({ integrationId, workspaceGid, projectGids });
      await sync.mutateAsync({ integrationId });
      setOffset(0);
      await scope.refetch();
      await queryClient.invalidateQueries({ queryKey: trpc.integrations.asana.tasks.queryKey() });
      toast.success('Asana collection queued');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not start Asana collection');
    }
  };

  return <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className='max-h-[85vh] overflow-y-auto sm:max-w-3xl'>
      <DialogHeader>
        <DialogTitle>Asana tasks and history</DialogTitle>
        <DialogDescription>Collect tasks from selected projects. Screenata reads Asana data; it does not change tasks or approve remediation.</DialogDescription>
      </DialogHeader>
      {!integrationId ? <Button disabled={connecting} onClick={async () => {
        setConnecting(true);
        try { await AsanaProvider.handleConnect({ workspaceId, onFinish: () => setConnecting(false) }); }
        catch (err) { setConnecting(false); toast.error(err instanceof Error ? err.message : 'Could not connect to Asana'); }
      }}>{connecting ? 'Connecting…' : 'Connect Asana'}</Button> : <div className='space-y-5'>
        {error ? <p role='alert' className='text-destructive text-sm'>{error.message}</p> : null}
        <div className='space-y-2'>
          <label htmlFor='asana-workspace' className='text-sm font-medium'>Asana workspace</label>
          <Select value={workspaceGid} disabled={busy || workspaces.isLoading} onValueChange={value => { setWorkspaceGid(value); setProjectGids([]); }}>
            <SelectTrigger id='asana-workspace'><SelectValue placeholder={workspaces.isLoading ? 'Loading workspaces…' : 'Select a workspace'} /></SelectTrigger>
            <SelectContent>{workspaces.data?.map(workspace => <SelectItem key={workspace.gid} value={workspace.gid}>{workspace.name}</SelectItem>)}</SelectContent>
          </Select>
          {workspaces.data?.length === 0 ? <p className='text-sm text-muted-foreground'>No accessible Asana workspaces.</p> : null}
        </div>
        {workspaceGid ? <fieldset className='space-y-2' disabled={busy || save.isPending || sync.isPending}>
          <legend className='mb-2 text-sm font-medium'>Projects</legend>
          {projects.isLoading ? <p className='text-sm text-muted-foreground'>Loading projects…</p> : null}
          {projects.data?.length === 0 ? <p className='text-sm text-muted-foreground'>No accessible projects in this workspace.</p> : null}
          <div className='max-h-48 space-y-2 overflow-y-auto'>
            {projects.data?.map(project => <label key={project.gid} className='flex items-center gap-2 text-sm'>
              <Checkbox checked={projectGids.includes(project.gid)} onCheckedChange={checked => setProjectGids(current => checked === true ? [...current, project.gid] : current.filter(gid => gid !== project.gid))} />
              {project.name}{project.archived ? ' (archived)' : ''}
            </label>)}
          </div>
        </fieldset> : null}
        <div className='flex flex-wrap items-center gap-3'>
          <Button disabled={busy || save.isPending || sync.isPending || !projectGids.length || projects.isLoading || !!projects.error} onClick={() => void saveAndSync()}>
            {busy ? 'Collecting…' : save.isPending || sync.isPending ? 'Starting…' : 'Save projects and collect'}
          </Button>
          <p role='status' className='text-sm text-muted-foreground'>{busy ? 'Collection runs in the background. You can close this dialog.' : scope.data?.lastSyncedAt ? `Last collected ${new Date(scope.data.lastSyncedAt).toLocaleString()}` : 'No completed collection for this selection.'}</p>
        </div>
        {scope.data?.lastError ? <p role='alert' className='text-destructive text-sm'>Collection failed: {scope.data.lastError} Previously collected data is retained.</p> : null}
        <section className='space-y-3 border-t pt-4'>
          <h3 className='text-sm font-medium'>Collected tasks</h3>
          <p className='text-sm text-muted-foreground'>Available activity includes comments and system events. It is not an immutable audit log or proof of verified remediation.</p>
          {tasks.isLoading ? <p className='text-sm'>Loading tasks…</p> : null}
          {tasks.data?.tasks.length === 0 ? <p className='text-sm text-muted-foreground'>No collected tasks for the saved project selection.</p> : null}
          {tasks.data?.tasks.map(task => <details key={task.id} className='rounded-md border p-3'>
            <summary className='cursor-pointer text-sm font-medium'>{task.name} · {task.completed ? 'Completed' : 'Incomplete'} · {task.assignee?.name ?? 'Unassigned'}</summary>
            <div className='mt-3 space-y-3 text-sm'>
              <a className='text-primary underline' href={task.permalink_url} target='_blank' rel='noopener noreferrer'>Open in Asana</a>
              <p className='text-xs text-muted-foreground'>Collected {new Date(task.collectedAt).toLocaleString()}. Retained snapshots may include tasks later moved or deleted in Asana.</p>
              <p className='whitespace-pre-wrap'>{task.notes}</p>
              <h4 className='font-medium'>Available task history</h4>
              {!task.stories.length ? <p className='text-muted-foreground'>Asana returned no activity. This does not establish approval or complete audit history.</p> : <ol className='space-y-3'>
                {task.stories.map(story => <li key={story.gid}>
                  <p className='text-xs text-muted-foreground'>{new Date(story.created_at).toLocaleString()} · {story.created_by?.name ?? 'System'} · {story.resource_subtype}</p>
                  <p className='whitespace-pre-wrap'>{story.text || 'No text returned for this event.'}</p>
                </li>)}
              </ol>}
            </div>
          </details>)}
          <div className='flex gap-2'>
            <Button variant='outline' disabled={!offset || tasks.isFetching} onClick={() => setOffset(current => Math.max(0, current - 25))}>Previous</Button>
            <Button variant='outline' disabled={!tasks.data?.hasMore || tasks.isFetching} onClick={() => setOffset(current => current + 25)}>Next</Button>
          </div>
        </section>
      </div>}
    </DialogContent>
  </Dialog>;
}

export const AsanaProvider: IntegrationProvider = {
  id: 'Asana', name: 'Asana', description: 'Read tasks and available history from selected projects',
  Icon: () => <img src='/vendor-logos/asana.svg' alt='' className='size-6' />,
  ConfigModal: AsanaConfigModal, postAuthAction: 'CONFIGURE',
  getFeatures: () => [{ id: 'tasks', name: 'Tasks and history', description: 'Read-only task collection', enabled: true }],
  handleConnect: async ({ onSuccess, onFinish }) => {
    const result = await queryClient.getMutationCache().build(queryClient, trpc.integrations.asana.initAuth.mutationOptions()).execute(undefined).catch(error => {
      toast.error(error instanceof Error ? error.message : 'Could not start Asana authorization');
      onFinish?.('error');
      throw error;
    });
    openOAuthConnectPopup({ authUrl: result.authUrl, windowName: 'asana-auth', provider: 'Asana',
      onSuccess, onFinish, onError: message => toast.error(message),
      onPopupBlocked: () => toast.error('Allow popups to connect Asana.'),
    });
  },
  onConnectComplete: async ({ code, state }) => {
    const result = await queryClient.getMutationCache().build(queryClient, trpc.integrations.asana.completeAuth.mutationOptions()).execute({ code, state });
    await queryClient.invalidateQueries({ queryKey: trpc.integrations.getWorkspaceIntegrations.queryKey() });
    return result;
  },
  syncData: async integration => {
    await queryClient.getMutationCache().build(queryClient, trpc.integrations.asana.sync.mutationOptions()).execute({ integrationId: integration.id });
    await queryClient.invalidateQueries({ queryKey: trpc.integrations.asana.getScope.queryKey() });
  },
};
